/**
 * Rynora AI Agents Architecture — nine product agents, six metering
 * classes, deterministic-first governance.
 *
 * Agents understand and propose. Review validates. Humans govern.
 * Rynora executes. Evidence proves. Policy decides.
 *
 * Distinct from `agent.ts` (Rynora Agent findings / nightly crawl loop).
 */

import { z } from 'zod';

/** Six metering classes — separate from model tier (Fast/Medium/High). */
export const AGENT_METERING_CLASSES = ['authoring', 'compile', 'runtime', 'background', 'narration', 'gate'] as const;
export type AgentMeteringClass = (typeof AGENT_METERING_CLASSES)[number];
export const AgentMeteringClassSchema = z.enum(AGENT_METERING_CLASSES);

/**
 * Nine bounded product agents. Review Gate is governance, not a peer
 * LLM producer — its binding arm is deterministic.
 */
export const PORTFOLIO_AGENT_IDS = [
  'applicationIntelligence',
  'requirementIntelligence',
  'scenarioIntelligence',
  'automationEngineer',
  'changeImpact',
  'failureInvestigation',
  'healing',
  'qualityIntelligence',
  'reviewGate',
] as const;
export type PortfolioAgentId = (typeof PORTFOLIO_AGENT_IDS)[number];
export const PortfolioAgentIdSchema = z.enum(PORTFOLIO_AGENT_IDS);

/** Deterministic Review Gate dispositions. */
export const AGENT_REVIEW_DISPOSITIONS = ['ACCEPT', 'REVISE', 'REJECT', 'HUMAN'] as const;
export type AgentReviewDisposition = (typeof AGENT_REVIEW_DISPOSITIONS)[number];
export const AgentReviewDispositionSchema = z.enum(AGENT_REVIEW_DISPOSITIONS);

export const AgentReviewFindingSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  /** Field or claim path that failed, when applicable. */
  path: z.string().optional(),
});
export type AgentReviewFinding = z.infer<typeof AgentReviewFindingSchema>;

export const AgentReviewResultSchema = z.object({
  disposition: AgentReviewDispositionSchema,
  findings: z.array(AgentReviewFindingSchema),
  /** Binding arm never uses an LLM; advisory notes are optional. */
  advisoryNotes: z.array(z.string()).default([]),
});
export type AgentReviewResult = z.infer<typeof AgentReviewResultSchema>;

/**
 * Whether a metering class may debit the customer-visible
 * `aiCallsPerMonth` / `ai_usage` counter.
 */
export function meteringClassDebitsPlanCounter(meteringClass: AgentMeteringClass): boolean {
  return meteringClass === 'authoring';
}

/**
 * Classes that must never be blocked by authoring quota exhaustion.
 * Binding certification: Healing (runtime) remains eligible when
 * Generate Scenario returns plan-limit.
 */
export function meteringClassAlwaysAllowed(meteringClass: AgentMeteringClass): boolean {
  return (
    meteringClass === 'runtime' ||
    meteringClass === 'gate' ||
    meteringClass === 'compile' ||
    meteringClass === 'background' ||
    meteringClass === 'narration'
  );
}

/** Gap / quality issue codes for Requirement Intelligence proposals. */
export const REQUIREMENT_INTELLIGENCE_ISSUE_KINDS = [
  'ambiguity',
  'missing_acceptance',
  'untestable',
  'overlap',
  'incomplete',
  'other',
] as const;
export type RequirementIntelligenceIssueKind = (typeof REQUIREMENT_INTELLIGENCE_ISSUE_KINDS)[number];
export const RequirementIntelligenceIssueKindSchema = z.enum(REQUIREMENT_INTELLIGENCE_ISSUE_KINDS);

export const RequirementIntelligenceIssueSchema = z.object({
  kind: RequirementIntelligenceIssueKindSchema,
  message: z.string().min(1).max(500),
  path: z.string().max(120).optional(),
});
export type RequirementIntelligenceIssue = z.infer<typeof RequirementIntelligenceIssueSchema>;

/**
 * Structured requirement proposal — never silently edits the authority row.
 * Status remains draft/analyzed until a human confirms.
 */
export const RequirementIntelligenceProposalSchema = z.object({
  proposedTitle: z.string().min(1).max(200),
  proposedDescription: z.string().max(2_000),
  acceptanceIntent: z.array(z.string().min(1).max(300)).max(12),
  testabilityNotes: z.array(z.string().min(1).max(300)).max(12),
  issues: z.array(RequirementIntelligenceIssueSchema).max(20),
  relatedRequirementKeys: z.array(z.string().min(1).max(80)).max(20),
  evidenceRefs: z.array(z.string().min(1).max(200)).max(40),
});
export type RequirementIntelligenceProposal = z.infer<typeof RequirementIntelligenceProposalSchema>;

export const RequirementIntelligenceProposeRequestSchema = z.object({
  /** Existing requirement to improve; omit when proposing from free text. */
  requirementId: z.string().uuid().optional(),
  /** Free-text requirement when no id is supplied (or to override description). */
  text: z.string().trim().min(1).max(4_000).optional(),
  title: z.string().trim().min(1).max(200).optional(),
  key: z.string().trim().min(1).max(80).optional(),
});
export type RequirementIntelligenceProposeRequest = z.infer<typeof RequirementIntelligenceProposeRequestSchema>;

export const RequirementIntelligenceProposeResponseSchema = z.object({
  source: z.enum(['llm', 'heuristic', 'skipped']),
  skipReason: z.string().nullable(),
  proposal: RequirementIntelligenceProposalSchema.nullable(),
  review: AgentReviewResultSchema,
  meteringClass: z.literal('authoring'),
  agentId: z.literal('requirementIntelligence'),
});
export type RequirementIntelligenceProposeResponse = z.infer<typeof RequirementIntelligenceProposeResponseSchema>;

/** Change Impact agent — narration over deterministic findings only. */
export const ChangeImpactAgentExplainRequestSchema = z.object({
  /** Optional commit SHA (same semantics as GET change-impact). */
  sha: z.string().trim().min(7).max(64).optional(),
  /** Force LLM even when deterministic summary is already sufficient. */
  force: z.boolean().optional(),
});
export type ChangeImpactAgentExplainRequest = z.infer<typeof ChangeImpactAgentExplainRequestSchema>;

export const ChangeImpactAgentExplanationSchema = z.object({
  summary: z.string().min(1).max(1_200),
  attentionAreas: z.array(z.string().min(1).max(300)).max(12),
  affectedScopeNotes: z.array(z.string().min(1).max(300)).max(12),
  evidenceRefs: z.array(z.string().min(1).max(200)).max(40),
  /**
   * Honesty lock — agent never emits a competing release/QD verdict.
   * Always false on the wire; clients must ignore any invented verdict.
   */
  replacesDeterministicVerdict: z.literal(false),
});
export type ChangeImpactAgentExplanation = z.infer<typeof ChangeImpactAgentExplanationSchema>;

export const ChangeImpactAgentExplainResponseSchema = z.object({
  source: z.enum(['llm', 'heuristic', 'skipped']),
  skipReason: z.string().nullable(),
  explanation: ChangeImpactAgentExplanationSchema.nullable(),
  review: AgentReviewResultSchema,
  meteringClass: z.literal('background'),
  agentId: z.literal('changeImpact'),
  /**
   * Snapshot of deterministic counts the narration was grounded on.
   * Numbers remain authoritative even when narration is skipped.
   */
  deterministicSnapshot: z.object({
    scenarioCount: z.number().int().nonnegative(),
    flowCount: z.number().int().nonnegative(),
    openFailureCount: z.number().int().nonnegative(),
    hasReview: z.boolean(),
  }),
});
export type ChangeImpactAgentExplainResponse = z.infer<typeof ChangeImpactAgentExplainResponseSchema>;
